import { isPositiveFinite } from '../geometry/number';
import type { Aabb2, Point2 } from '../geometry/point';
import {
  DEFAULT_VIEWPORT,
  type QuarterTurns,
  type ViewportSize,
  type ViewportState,
} from './types';

/**
 * Fraction of the container left empty around the drawing when fitting, split
 * across both sides of each axis.
 */
export const DEFAULT_FIT_PADDING = 0.08;

export type FitOptions = {
  padding?: number;
  /** Quarter turns the fitted viewport should keep. Defaults to none. */
  quarterTurns?: QuarterTurns;
};

/**
 * Fits `bounds` inside `size`, centred, preserving aspect ratio.
 *
 * A quarter-turned view is fitted against swapped container extents, so a wide
 * drawing turned on its side is framed by the container's height rather than
 * overflowing it — the rotated footprint is what has to fit, not the unrotated one.
 *
 * Degenerate inputs fall back rather than producing a non-finite transform:
 * a zero-extent axis (a perfectly horizontal or vertical drawing) is scaled by
 * the other axis alone, and fully degenerate bounds or an unmeasured container
 * keep `DEFAULT_VIEWPORT`'s scale while still centring on the geometry.
 */
export const fitAabb = (
  bounds: Aabb2 | null | undefined,
  size: ViewportSize,
  { padding = DEFAULT_FIT_PADDING, quarterTurns = 0 }: FitOptions = {},
): ViewportState => {
  if (!bounds) {
    return { ...DEFAULT_VIEWPORT, quarterTurns };
  }

  const [minX, minY, maxX, maxY] = bounds;
  const center: Point2 = [(minX + maxX) / 2, (minY + maxY) / 2];

  const worldWidth = maxX - minX;
  const worldHeight = maxY - minY;

  // Odd quarter turns put world x along the container's vertical axis.
  const isTurned = quarterTurns % 2 === 1;
  const usableWidth = (isTurned ? size.height : size.width) * (1 - padding);
  const usableHeight = (isTurned ? size.width : size.height) * (1 - padding);

  if (!isPositiveFinite(usableWidth) || !isPositiveFinite(usableHeight)) {
    return { center, scale: DEFAULT_VIEWPORT.scale, quarterTurns };
  }

  const scaleX = isPositiveFinite(worldWidth) ? usableWidth / worldWidth : null;
  const scaleY = isPositiveFinite(worldHeight)
    ? usableHeight / worldHeight
    : null;

  if (scaleX !== null && scaleY !== null) {
    return { center, scale: Math.min(scaleX, scaleY), quarterTurns };
  }
  // A single-axis drawing (zero extent on the other axis) still has one usable
  // scale; point-like bounds have neither and keep the default.
  return {
    center,
    scale: scaleX ?? scaleY ?? DEFAULT_VIEWPORT.scale,
    quarterTurns,
  };
};
